import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import CountdownTimer from './CountdownTimer';
import { ReactComponent as CurrencyExchangeSwitchIcon } from 'assets/switch-icon.svg';
import AmountInput from 'components/common/AmountInput';
import { EXCHANGE_AMOUNT_DIRECTION } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { filterPaymentAccounts } from 'components/common/PaymentForm/utils';
import PopUpSuccessScheme from 'components/common/PopUpScheme/PopUpSuccessScheme';
import PopUpCurrencyExchangePaymentConfirmationScheme from 'components/common/PopUpScheme/PopUpCurrencyExchangePaymentConfirmationScheme';
import i18nContext from 'components/i18n-context';
import { getAccountTitleWithFormatting, hasMultipleCurrencies } from 'components/utils';
import { getConfirmationActionType } from 'services/authUtils';
import { CRYPTO_CURRENCY_PRECISION, CURRENCIES } from 'services/currenciesConstant';
import { getErrorMessageForAlert, isCryptoProvider, amountFormattedValue } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import { IconButton } from 'uikit/IconButton/IconButton';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { PopUp } from 'uikit/PopUp/PopUp';
import './CurrencyExchange.scss';

const ACCOUNT_TYPE = {
  FROM: 'accountFrom',
  TO: 'accountTo'
};

// Helper function to check if two accounts conflict for exchange
const areAccountsConflicting = (account1, account2) => {
  if (!account1 || !account2) return false;

  const isCrypto1 = isCryptoProvider(account1.transfer_provider);
  const isCrypto2 = isCryptoProvider(account2.transfer_provider);

  // For crypto wallets, allow same currency with different networks
  if (isCrypto1 && isCrypto2) {
    return account1.currency === account2.currency && account1.network === account2.network;
  }

  // For fiat, don't allow same currency
  return account1.currency === account2.currency;
};

// Helper function to find a suitable account for exchange
const findAccountForExchange = (accounts, excludeAccount) => {
  return accounts.find(
    (account) =>
      account.wallet_number !== excludeAccount?.wallet_number && !areAccountsConflicting(account, excludeAccount)
  );
};

const CurrencyExchange = ({ userAccounts, customerNumber, currencyExchangeStore, userStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currencyExchangeStore.isRepeatTransaction) {
      currencyExchangeStore.resetExchangeStore();
    } else {
      currencyExchangeStore.createExchangeQuote(customerNumber).then(() => {
        if (currencyExchangeStore.amountFrom && !currencyExchangeStore.error) {
          currencyExchangeStore
            .calculateExchangeData(
              customerNumber,
              currencyExchangeStore.amountFrom,
              EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM
            )
            .then(() => {
              currencyExchangeStore.setIsRepeatTransactionStatus(false);
            });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currencyExchangeStore.isRepeatTransaction) {
      const paymentAccounts = filterPaymentAccounts(userAccounts);

      if (hasMultipleCurrencies(paymentAccounts)) {
        const accountFrom = paymentAccounts[0];
        const accountTo = findAccountForExchange(paymentAccounts, accountFrom);
        currencyExchangeStore.setFromToAccounts(accountFrom, accountTo);
        if (currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo) {
          currencyExchangeStore.createExchangeQuote(customerNumber);
        }
      } else {
        navigate(ROUTE_PATHS.DASHBOARD);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccounts]);

  useEffect(() => {
    if (currencyExchangeStore.isExchangeSucceeded) {
      userStore.getUserAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyExchangeStore.isExchangeSucceeded]);

  const handleChangeAccount = async (name, value) => {
    const oppositeAccountType = ACCOUNT_TYPE.FROM === name ? ACCOUNT_TYPE.TO : ACCOUNT_TYPE.FROM;
    const paymentAccounts = filterPaymentAccounts(userAccounts);
    const selectedAccount = paymentAccounts.find((account) => account.wallet_number === value);
    const oppositeAccount = areAccountsConflicting(selectedAccount, currencyExchangeStore?.[oppositeAccountType])
      ? findAccountForExchange(paymentAccounts, selectedAccount)
      : currencyExchangeStore?.[oppositeAccountType];
    if (selectedAccount?.wallet_number !== currencyExchangeStore?.[name]?.wallet_number) {
      if (ACCOUNT_TYPE.FROM === name) {
        currencyExchangeStore.setFromToAccounts(selectedAccount, oppositeAccount);
      } else {
        currencyExchangeStore.setFromToAccounts(oppositeAccount, selectedAccount);
      }
      if (currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo) {
        await currencyExchangeStore.createExchangeQuote(customerNumber);
        if (currencyExchangeStore.amountFrom && !currencyExchangeStore.error) {
          await currencyExchangeStore.calculateExchangeData(
            customerNumber,
            currencyExchangeStore.amountFrom,
            EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM
          );
        }
      }
    }
  };

  const getCommission = async (amount, direction) => {
    if (currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo && amount && Number(amount) !== 0) {
      await currencyExchangeStore?.calculateExchangeData(customerNumber, amount, direction);
    }
  };

  const handleAmountChange = async ({ target: { name, value } }) => {
    const previousValue = currencyExchangeStore[name];

    if (value === previousValue) return;

    currencyExchangeStore.setAmount(name, value);

    if (value && value?.endsWith('.') && Number(value) !== 0) {
      await getCommission(value?.slice(0, -1), name);
    } else {
      await getCommission(value, name);
    }
  };

  const handleAmountOnBlur = async (name) => {
    const value = currencyExchangeStore[name];
    const stringValue = value?.toString();
    if (stringValue && stringValue.endsWith('.') && Number(stringValue) !== 0) {
      currencyExchangeStore.setAmount(name, stringValue.slice(0, -1));
    }
  };

  const handleCurrencyExchangeSwitch = async () => {
    currencyExchangeStore.setFromToAccounts(currencyExchangeStore.accountTo, currencyExchangeStore.accountFrom);
    if (currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo) {
      await currencyExchangeStore.createExchangeQuote(customerNumber);
      if (currencyExchangeStore.amountFrom && !currencyExchangeStore.error) {
        await currencyExchangeStore.calculateExchangeData(
          customerNumber,
          currencyExchangeStore.amountFrom,
          EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM
        );
      }
    }
  };

  const getAvailableBalance = (account) =>
    account &&
    i18n.getMessage('sendMoney.topLabel', {
      available: account.availableDetails?.displayAmount || account.available
    });

  // The balance has to cover the sell amount AND the commission (always charged in the sell currency),
  // so spell out the required total instead of a bare "Insufficient funds".
  const getInsufficientFundsMessage = () => {
    const { accountFrom, amountFrom, amountFromDetails, exchangeData } = currencyExchangeStore;
    const commission = exchangeData?.commission || 0;
    const currency = accountFrom?.currency;

    if (!currency || !amountFrom) return i18n.getMessage('error.INSUFFICIENT_FUNDS');

    const precision = isCryptoProvider(accountFrom?.transfer_provider)
      ? CRYPTO_CURRENCY_PRECISION
      : (accountFrom?.currency_precision ?? 2);
    const formatAmount = (value) => {
      const symbol = CURRENCIES[currency]?.symbol;
      const formatted = amountFormattedValue(value, precision);

      return symbol ? `${symbol}${formatted}` : `${formatted} ${currency}`;
    };
    // Sum in integer minor units to avoid floating-point drift (e.g. 7280 + 14.56).
    const factor = 10 ** precision;
    const total = (Math.round(Number(amountFrom) * factor) + Math.round(Number(commission) * factor)) / factor;

    return i18n.getMessage('currencyExchange.error.insufficientFunds', {
      total: formatAmount(total),
      amount: amountFromDetails?.displayAmount || formatAmount(amountFrom),
      commission: exchangeData?.commissionDetails?.displayAmount || formatAmount(commission),
      available: accountFrom?.availableDetails?.displayAmount || formatAmount(accountFrom?.available)
    });
  };

  const accountsOptions = filterPaymentAccounts(userAccounts)?.map((account) => {
    const title = getAccountTitleWithFormatting(account);

    return {
      key: account?.wallet_number,
      value: title,
      currency: account?.currency,
      networkCode: account?.network
    };
  });

  const handleExchangeTransferCreate = async () => {
    await currencyExchangeStore.createExchangeTransfer(customerNumber);
  };

  const refreshQuote = async () => {
    if (currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo) {
      await currencyExchangeStore.createExchangeQuote(customerNumber);
    }
  };

  const isAccountsExist = currencyExchangeStore.accountFrom && currencyExchangeStore.accountTo;

  return (
    <section className={'page-container currency-exchange'}>
      <NewContainer className={'content-card-wrapper'} header={i18n.getMessage('container.exchange')}>
        <div className={'exchange-wrapper'}>
          <Alert
            type={'warning'}
            className={'exchange-wrapper-alert'}
            message={
              !isAccountsExist
                ? i18n.getMessage('transfer.provider.notFound')
                : currencyExchangeStore.isAmountExceeded
                  ? getInsufficientFundsMessage()
                  : currencyExchangeStore?.temporaryError
                    ? getErrorMessageForAlert(i18n, currencyExchangeStore.temporaryError)
                    : currencyExchangeStore?.error
                      ? getErrorMessageForAlert(i18n, currencyExchangeStore.error)
                      : ''
            }
          />
          <div className={'exchange-form-wrapper'}>
            <div className={'exchange-inputs-block'}>
              <InputDropDown
                isRequired={true}
                name={ACCOUNT_TYPE.FROM}
                bottomLabel={getAvailableBalance(currencyExchangeStore.accountFrom)}
                value={currencyExchangeStore.accountFrom?.wallet_number}
                currency={currencyExchangeStore.accountFrom?.currency}
                networkCode={currencyExchangeStore.accountFrom?.network || null}
                options={accountsOptions}
                onChange={handleChangeAccount}
                label={i18n.getMessage('currencyExchange.form.from')}
                isMulti={false}
                isDisabled={currencyExchangeStore.isExchangeLoading && currencyExchangeStore.isExchangeTransferCreated}
                isCryptoWallet={isCryptoProvider(currencyExchangeStore.accountFrom?.transfer_provider)}
              />
              <AmountInput
                isRequired={true}
                label={i18n.getMessage('currencyExchange.form.sellAmount')}
                name={EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM}
                value={currencyExchangeStore.amountFrom?.toString()}
                onChange={handleAmountChange}
                placeholder={i18n.getMessage('currencyExchange.form.amountPlaceholder')}
                onBlur={() => handleAmountOnBlur(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM)}
                precision={
                  isCryptoProvider(currencyExchangeStore.accountFrom?.transfer_provider)
                    ? CRYPTO_CURRENCY_PRECISION
                    : (currencyExchangeStore.accountFrom?.currency_precision ?? 2)
                }
                currency={currencyExchangeStore.accountFrom?.currency}
                error={currencyExchangeStore.isAmountExceeded}
                isDisabled={
                  currencyExchangeStore.fieldsAreDisabled ||
                  (currencyExchangeStore.isExchangeLoading && currencyExchangeStore.isExchangeTransferCreated)
                }
              />
            </div>
            <IconButton
              className={'switch-button'}
              Icon={CurrencyExchangeSwitchIcon}
              onClick={handleCurrencyExchangeSwitch}
              isDisabled={currencyExchangeStore.isExchangeLoading}
            />
            <div className={'exchange-inputs-block'}>
              <InputDropDown
                isRequired={true}
                name={ACCOUNT_TYPE.TO}
                bottomLabel={getAvailableBalance(currencyExchangeStore.accountTo)}
                value={currencyExchangeStore.accountTo?.wallet_number}
                currency={currencyExchangeStore.accountTo?.currency}
                networkCode={currencyExchangeStore.accountTo?.network || null}
                options={accountsOptions}
                onChange={handleChangeAccount}
                label={i18n.getMessage('currencyExchange.form.to')}
                isMulti={false}
                isDisabled={currencyExchangeStore.isExchangeLoading && currencyExchangeStore.isExchangeTransferCreated}
                isCryptoWallet={isCryptoProvider(currencyExchangeStore.accountTo?.transfer_provider)}
              />
              <AmountInput
                isRequired={true}
                label={i18n.getMessage('currencyExchange.form.buyAmount')}
                name={EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO}
                value={currencyExchangeStore.amountTo?.toString()}
                placeholder={i18n.getMessage('currencyExchange.form.amountPlaceholder')}
                onChange={handleAmountChange}
                onBlur={() => handleAmountOnBlur(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO)}
                precision={
                  isCryptoProvider(currencyExchangeStore.accountTo?.transfer_provider)
                    ? CRYPTO_CURRENCY_PRECISION
                    : (currencyExchangeStore.accountTo?.currency_precision ?? 2)
                }
                currency={currencyExchangeStore.accountTo?.currency}
                isDisabled={
                  currencyExchangeStore.fieldsAreDisabled ||
                  (currencyExchangeStore.isExchangeLoading && currencyExchangeStore.isExchangeTransferCreated)
                }
              />
            </div>
            <div className={'exchange-rate-commission-wrapper'}>
              <div className={'exchange-rate-commission-block'}>
                <span className={'exchange-rate-commission-title'}>
                  {i18n.getMessage('currencyExchange.form.yourRate')}
                </span>
                {(currencyExchangeStore.exchangeData?.rateDetails || currencyExchangeStore.exchangeData?.rate) && (
                  <span className={'exchange-rate-commission-value'}>
                    {`1${CURRENCIES[currencyExchangeStore.accountFrom?.currency]?.symbol || currencyExchangeStore.accountFrom?.currency} = ${
                      currencyExchangeStore.exchangeData?.rateDetails?.displayRate ||
                      currencyExchangeStore.exchangeData?.rate
                    }${CURRENCIES[currencyExchangeStore.accountTo?.currency]?.symbol || currencyExchangeStore.accountTo?.currency}`}
                  </span>
                )}
              </div>
              <div className={'exchange-rate-commission-block'}>
                <span className={'exchange-rate-commission-title'}>
                  {i18n.getMessage('currencyExchange.form.commission', {
                    sellCurrency: currencyExchangeStore.accountFrom?.currency,
                    buyCurrency: currencyExchangeStore.accountTo?.currency
                  })}
                </span>
                {(currencyExchangeStore?.exchangeData?.commissionDetails ||
                  currencyExchangeStore?.exchangeData?.commission !== null) && (
                  <span className={'exchange-rate-commission-value'}>
                    {currencyExchangeStore?.exchangeData?.commissionDetails?.displayAmount ||
                      `${amountFormattedValue(
                        currencyExchangeStore?.exchangeData?.commission,
                        isCryptoProvider(currencyExchangeStore.accountFrom?.transfer_provider)
                          ? CRYPTO_CURRENCY_PRECISION
                          : 2
                      )} ${currencyExchangeStore.accountFrom?.currency}`}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={'exchange-rate-timer-wrapper'}>
            <CountdownTimer
              duration={currencyExchangeStore.exchangeData.duration}
              quoteId={currencyExchangeStore.exchangeData.quoteId}
              onComplete={refreshQuote}
            />
          </div>
          {isAccountsExist && (
            <div className={'exchange-button-wrapper'}>
              <Button
                type={'primary'}
                roleType={'submit'}
                size={'large'}
                isDisabled={
                  currencyExchangeStore.isAmountExceeded ||
                  currencyExchangeStore.fieldsAreDisabled ||
                  !!currencyExchangeStore?.error ||
                  currencyExchangeStore.isExchangeLoading ||
                  !currencyExchangeStore.isCalculatedFromToValueAndCommission
                }
                onClick={handleExchangeTransferCreate}
              >
                {currencyExchangeStore.isExchangeLoading ? (
                  <Loader />
                ) : (
                  i18n.getMessage('currencyExchange.form.exchangeNow')
                )}
              </Button>
            </div>
          )}
        </div>
      </NewContainer>
      <PopUp
        className={currencyExchangeStore?.isExchangeSucceeded ? 'transaction-success' : 'transaction-info'}
        show={currencyExchangeStore?.isExchangeTransferCreated || currencyExchangeStore?.isExchangeSucceeded}
        alignOnCenter={currencyExchangeStore?.isExchangeSucceeded}
        onClose={
          currencyExchangeStore?.isExchangeTransferCreated
            ? () => currencyExchangeStore?.resetCreatedExchangeTransfer()
            : () => currencyExchangeStore?.resetAfterSuccessfulExchange()
        }
      >
        {currencyExchangeStore?.isExchangeSucceeded ? (
          <PopUpSuccessScheme
            onClose={() => currencyExchangeStore.resetAfterSuccessfulExchange()}
            message={i18n.getMessage('popUp.message.moneyTransfer')}
          />
        ) : (
          <PopUpCurrencyExchangePaymentConfirmationScheme
            isLoading={currencyExchangeStore.isLoading}
            isCurrency={true}
            transferData={currencyExchangeStore.createdExchangeTransferData}
            error={currencyExchangeStore?.confirmationPopupError}
            resendSecurityCode={() => currencyExchangeStore.resendExchangeSecurityCode()}
            clearError={() => currencyExchangeStore?.clearConfirmationPopupError()}
            onConfirm={(securityCode) => currencyExchangeStore?.confirmExchangeTransfer(customerNumber, securityCode)}
            onClose={() => currencyExchangeStore?.resetCreatedExchangeTransfer()}
            isCryptoTransaction={
              isCryptoProvider(currencyExchangeStore?.accountFrom?.transfer_provider) ||
              isCryptoProvider(currencyExchangeStore?.accountTo?.transfer_provider)
            }
            isShowSwitchConfirmationMethodScreen={currencyExchangeStore?.isShowSwitchConfirmationMethodScreen}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              currencyExchangeStore?.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={(type) =>
              currencyExchangeStore?.forceSwitchToAnotherConfirmationType(type)
            }
            currentConfirmationType={getConfirmationActionType()}
          />
        )}
      </PopUp>
    </section>
  );
};

CurrencyExchange.propTypes = {
  userAccounts: PropTypes.array,
  customerNumber: PropTypes.string,
  currencyExchangeStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userAccounts: stores.userStore?.userAccounts,
  customerNumber: stores.userStore.userData.account?.account_number,
  currencyExchangeStore: stores.currencyExchangeStore,
  userStore: stores.userStore
}))(observer(CurrencyExchange));
